/**
 * Firestore database functions for Feline-SA
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';

// =====================
// CATTERY FUNCTIONS
// =====================

export const createCattery = async (data) => {
  const catteryData = {
    ...data,
    verified: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'catteries'), catteryData);
  
  // Update user with catteryId
  if (data.breederId) {
    await updateDoc(doc(db, 'users', data.breederId), {
      catteryId: docRef.id,
      role: 'breeder'
    });
  }

  return { id: docRef.id, ...catteryData };
};

export const getCattery = async (catteryId) => {
  const docRef = doc(db, 'catteries', catteryId);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

export const getCatteriesByAssociation = async (association) => {
  const q = query(
    collection(db, 'catteries'),
    where('association', '==', association),
    orderBy('name')
  );
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const updateCattery = async (catteryId, data) => {
  const docRef = doc(db, 'catteries', catteryId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp()
  });
};

// =====================
// CAT FUNCTIONS
// =====================

export const createCat = async (data) => {
  const catData = {
    ...data,
    status: data.status || 'not_for_sale',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'cats'), catData);
  return { id: docRef.id, ...catData };
};

export const getCat = async (catId) => {
  const docRef = doc(db, 'cats', catId);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

export const getCatsByCattery = async (catteryId) => {
  const q = query(
    collection(db, 'cats'),
    where('catteryId', '==', catteryId),
    orderBy('createdAt', 'desc')
  );
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getAvailableCats = async (filters = {}) => {
  let q = query(
    collection(db, 'cats'),
    where('status', 'in', ['available', 'reserved'])
  );

  if (filters.breed) {
    q = query(q, where('breed', '==', filters.breed));
  }

  if (filters.sex) {
    q = query(q, where('sex', '==', filters.sex));
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const updateCat = async (catId, data) => {
  const docRef = doc(db, 'cats', catId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const updateCatStatus = async (catId, status, price = null) => {
  const updateData = {
    status,
    updatedAt: serverTimestamp()
  };
  
  if (price !== null) {
    updateData.price = price;
  }

  const docRef = doc(db, 'cats', catId);
  await updateDoc(docRef, updateData);
};

export const deleteCat = async (catId) => {
  await deleteDoc(doc(db, 'cats', catId));
};

// Get cat's pedigree (sire/dam tree)
export const getCatPedigree = async (catId, depth = 3) => {
  const buildTree = async (id, currentDepth) => {
    if (!id || currentDepth > depth) return null;

    const cat = await getCat(id);
    if (!cat) return null;

    const sire = await buildTree(cat.sireId, currentDepth + 1);
    const dam = await buildTree(cat.damId, currentDepth + 1);

    return {
      ...cat,
      sire,
      dam
    };
  };

  return buildTree(catId, 1);
};

// =====================
// LITTER FUNCTIONS
// =====================

export const createLitter = async (data) => {
  const litterData = {
    ...data,
    kittenIds: data.kittenIds || [],
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'litters'), litterData);
  return { id: docRef.id, ...litterData };
};

export const getLittersByCattery = async (catteryId) => {
  const q = query(
    collection(db, 'litters'),
    where('catteryId', '==', catteryId),
    orderBy('dateOfBirth', 'desc')
  );
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const addKittenToLitter = async (litterId, kittenId) => {
  const docRef = doc(db, 'litters', litterId);
  const litter = await getDoc(docRef);
  
  if (litter.exists()) {
    const kittenIds = litter.data().kittenIds || [];
    await updateDoc(docRef, {
      kittenIds: [...kittenIds, kittenId]
    });
  }
};

// =====================
// SALES & COMMISSION FUNCTIONS
// =====================

export const createSale = async (data) => {
  const saleData = {
    ...data,
    status: 'reserved',
    reservedAt: serverTimestamp(),
    completedAt: null,
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'sales'), saleData);

  // Update cat status
  await updateCatStatus(data.catId, 'reserved');

  return { id: docRef.id, ...saleData };
};

export const completeSale = async (saleId) => {
  const saleRef = doc(db, 'sales', saleId);
  const saleSnap = await getDoc(saleRef);

  if (!saleSnap.exists()) {
    throw new Error('Sale not found');
  }

  const sale = saleSnap.data();

  // Update sale status
  await updateDoc(saleRef, {
    status: 'completed',
    completedAt: serverTimestamp()
  });

  // Update cat status
  await updateCatStatus(sale.catId, 'sold');

  // Create commission entry
  const commissionEntry = await createCommissionEntry({
    breederId: sale.breederId,
    catteryId: sale.catteryId,
    type: 'sale',
    saleId: saleId,
    saleAmount: sale.price,
    commissionRate: 0.10
  });

  // Link commission to sale
  await updateDoc(saleRef, {
    commissionEntryId: commissionEntry.id
  });

  return { ...sale, id: saleId, commissionEntryId: commissionEntry.id };
};

export const getSalesByCattery = async (catteryId, status = null) => {
  let q = query(
    collection(db, 'sales'),
    where('catteryId', '==', catteryId),
    orderBy('createdAt', 'desc')
  );

  if (status) {
    q = query(q, where('status', '==', status));
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// =====================
// COMMISSION LEDGER FUNCTIONS
// =====================

export const createCommissionEntry = async (data) => {
  const commissionAmount = data.saleAmount * data.commissionRate;
  const now = new Date();
  const monthStr = now.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });

  const entryData = {
    breederId: data.breederId,
    catteryId: data.catteryId,
    type: data.type, // 'sale' or 'registration'
    saleId: data.saleId || null,
    registrationId: data.registrationId || null,
    saleAmount: data.saleAmount,
    commissionAmount,
    commissionRate: data.commissionRate,
    status: 'pending',
    month: monthStr,
    dueDate: Timestamp.fromDate(new Date(now.getFullYear(), now.getMonth() + 1, 7)), // 7th of next month
    paidAt: null,
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'commission_ledger'), entryData);
  return { id: docRef.id, ...entryData };
};

export const getCommissionByBreeder = async (breederId, status = null) => {
  let q = query(
    collection(db, 'commission_ledger'),
    where('breederId', '==', breederId),
    orderBy('createdAt', 'desc')
  );

  if (status) {
    q = query(q, where('status', '==', status));
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getCommissionByMonth = async (breederId, month) => {
  const q = query(
    collection(db, 'commission_ledger'),
    where('breederId', '==', breederId),
    where('month', '==', month)
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const markCommissionPaid = async (entryId) => {
  const docRef = doc(db, 'commission_ledger', entryId);
  await updateDoc(docRef, {
    status: 'paid',
    paidAt: serverTimestamp()
  });
};

export const getMonthlyCommissionSummary = async (breederId) => {
  const entries = await getCommissionByBreeder(breederId);
  
  const summary = {};
  entries.forEach(entry => {
    if (!summary[entry.month]) {
      summary[entry.month] = {
        month: entry.month,
        totalSales: 0,
        totalCommission: 0,
        pending: 0,
        paid: 0,
        entries: []
      };
    }
    
    summary[entry.month].totalSales += entry.saleAmount;
    summary[entry.month].totalCommission += entry.commissionAmount;
    summary[entry.month][entry.status] += entry.commissionAmount;
    summary[entry.month].entries.push(entry);
  });

  return Object.values(summary).sort((a, b) => 
    new Date('01-' + b.month) - new Date('01-' + a.month)
  );
};

// =====================
// REGISTRATION FUNCTIONS
// =====================

export const createRegistration = async (data) => {
  const serviceFee = data.fee * 0.10; // 10% service fee

  const regData = {
    catId: data.catId,
    catteryId: data.catteryId,
    association: data.association,
    status: 'pending',
    fee: data.fee,
    serviceFee,
    totalFee: data.fee + serviceFee,
    pdfUrl: null,
    submittedAt: null,
    approvedAt: null,
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'registrations'), regData);
  return { id: docRef.id, ...regData };
};

export const submitRegistration = async (registrationId, pdfUrl) => {
  const docRef = doc(db, 'registrations', registrationId);
  await updateDoc(docRef, {
    status: 'submitted',
    pdfUrl,
    submittedAt: serverTimestamp()
  });
};

export const getRegistrationsByCattery = async (catteryId) => {
  const q = query(
    collection(db, 'registrations'),
    where('catteryId', '==', catteryId),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// =====================
// ENQUIRY FUNCTIONS
// =====================

export const createEnquiry = async (data) => {
  const enquiryData = {
    ...data,
    status: 'new',
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'enquiries'), enquiryData);
  return { id: docRef.id, ...enquiryData };
};

export const getEnquiriesByCattery = async (catteryId, status = null) => {
  let q = query(
    collection(db, 'enquiries'),
    where('catteryId', '==', catteryId),
    orderBy('createdAt', 'desc')
  );

  if (status) {
    q = query(q, where('status', '==', status));
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const updateEnquiryStatus = async (enquiryId, status) => {
  const docRef = doc(db, 'enquiries', enquiryId);
  await updateDoc(docRef, {
    status,
    updatedAt: serverTimestamp()
  });
};

// =====================
// BREED DATA
// =====================

export const SA_CAT_BREEDS = [
  { code: 'ABY', name: 'Abyssinian' },
  { code: 'BEN', name: 'Bengal' },
  { code: 'BIR', name: 'Birman' },
  { code: 'BRI', name: 'British Shorthair' },
  { code: 'BUR', name: 'Burmese' },
  { code: 'CHA', name: 'Chartreux' },
  { code: 'CRX', name: 'Cornish Rex' },
  { code: 'DRX', name: 'Devon Rex' },
  { code: 'EXO', name: 'Exotic Shorthair' },
  { code: 'MCO', name: 'Maine Coon' },
  { code: 'NFO', name: 'Norwegian Forest Cat' },
  { code: 'OCI', name: 'Ocicat' },
  { code: 'PER', name: 'Persian' },
  { code: 'RAG', name: 'Ragdoll' },
  { code: 'RUS', name: 'Russian Blue' },
  { code: 'SBI', name: 'Sacred Birman' },
  { code: 'SIA', name: 'Siamese' },
  { code: 'SIB', name: 'Siberian' },
  { code: 'SPH', name: 'Sphynx' },
  { code: 'TUV', name: 'Turkish Van' }
];

export const SA_ASSOCIATIONS = [
  { code: 'SACC', name: 'South African Cat Council', registrationFee: 250 },
  { code: 'CASA', name: 'Cat Association of Southern Africa', registrationFee: 200 },
  { code: 'CFSA', name: 'Cat Federation of Southern Africa', registrationFee: 180 }
];

export const SA_PROVINCES = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'Northern Cape',
  'North West',
  'Western Cape'
];
